// ─── PeppermintFi UI Component Library ────────────────────

const C = {
  mint: '#00C896', surface: '#111916', surfaceAlt: '#161F1A',
  border: '#1E2E24', text: '#E8F5EE', muted: '#6B8F7A',
  red: '#FF4D6A', yellow: '#FFD166', blue: '#4DAAFF', purple: '#A78BFA',
}

// StatCard
export function StatCard({ label, value, sub, subColor, accent = C.mint, style = {} }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
      padding: '20px 22px', position: 'relative', overflow: 'hidden', ...style
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: accent }} />
      <div style={{ color: C.muted, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8, fontFamily: 'DM Sans, sans-serif' }}>{label}</div>
      <div style={{ color: C.text, fontSize: 26, fontWeight: 700, fontFamily: 'Syne, sans-serif', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: subColor || C.mint, fontSize: 12, marginTop: 6 }}>{sub}</div>}
    </div>
  )
}

// Card
export function Card({ children, style = {} }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 22, ...style }}>
      {children}
    </div>
  )
}

// Button
export function Button({ children, variant = 'primary', onClick, disabled, style = {} }) {
  const styles = {
    primary: { background: C.mint, color: '#000', border: 'none' },
    secondary: { background: 'transparent', color: C.text, border: `1px solid ${C.border}` },
    danger: { background: 'transparent', color: C.red, border: `1px solid ${C.red}` },
  }
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...styles[variant], padding: '12px 22px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'DM Sans, sans-serif', fontSize: 14, fontWeight: 600, opacity: disabled ? 0.5 : 1,
      transition: 'all 0.15s ease', ...style
    }}>{children}</button>
  )
}

// Badge
export function Badge({ children, color = C.mint }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontSize: 11,
      background: `${color}18`, border: `1px solid ${color}33`, color,
      fontWeight: 600, letterSpacing: '0.06em',
    }}>{children}</span>
  )
}

// ProgressBar
export function ProgressBar({ value, max, color = C.mint, height = 6 }) {
  const pct = Math.min((value / max) * 100, 100)
  const over = value > max
  const barColor = over ? C.red : color
  return (
    <div style={{ background: C.border, borderRadius: 99, height, overflow: 'hidden' }}>
      <div style={{
        width: `${pct}%`, height: '100%', borderRadius: 99, background: barColor,
        boxShadow: `0 0 8px ${barColor}66`, transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)',
      }} />
    </div>
  )
}

// UpgradePrompt
export function UpgradePrompt({ feature, requiredPlan = 'Pro' }) {
  return (
    <div style={{
      background: C.surfaceAlt, border: `1px solid rgba(0,200,150,0.2)`,
      borderRadius: 16, padding: 40, textAlign: 'center',
    }}>
      <div style={{ fontSize: 36, marginBottom: 16 }}>🔒</div>
      <div style={{ color: C.text, fontSize: 18, fontFamily: 'Syne, sans-serif', fontWeight: 700, marginBottom: 8 }}>
        {feature} requires {requiredPlan}
      </div>
      <div style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>
        Upgrade to unlock {feature} and all {requiredPlan} features.
      </div>
      <Button onClick={() => window.location.href = '/pricing'}>
        View Pricing →
      </Button>
    </div>
  )
}

// PageHeader
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
      <div>
        <h1 style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 700, margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  )
}

// Empty state
export function EmptyState({ icon = '📊', title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ color: C.text, fontSize: 18, fontFamily: 'Syne, sans-serif', fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ color: C.muted, fontSize: 14, maxWidth: 360, margin: '0 auto 24px' }}>{description}</div>
      {action}
    </div>
  )
}
